/**
 * CRM Navigation Service
 *
 * Opens CRM entity detail pages in the browser (deep links for AI Assistant chips/cards).
 */

import { getInstance } from './auth.js';
import { buildCreateContactFromSignatureUrl } from './create-contact-from-signature-url.js';

const KNOWN_ENTITY_TYPES = new Set([
  'activity',
  'lead',
  'contact',
  'deal',
  'customer',
  'campaign',
  'task',
]);

class AssistGoCRMUrlBuilder {
  buildEntityUrl(instance, entityType, entityId) {
    const host = instance ? `${instance}.assistgo.de` : 'dev.assistgo.de';
    return `https://${host}/${entityType}/${entityId}/details`;
  }

  buildActivityUrl(instance, activityId) {
    return this.buildEntityUrl(instance, 'activity', activityId);
  }
}

class BrowserWindowService {
  open(url, target = '_blank', features = 'noopener,noreferrer') {
    window.open(url, target, features);
  }
}

export class CRMNavigationService {
  constructor(urlBuilder = null, windowService = null) {
    this.urlBuilder = urlBuilder || new AssistGoCRMUrlBuilder();
    this.windowService = windowService || new BrowserWindowService();
  }

  async openActivity(activityId, getInstanceFn) {
    const instance = await this._resolveInstance(getInstanceFn);
    const url = this.urlBuilder.buildEntityUrl(instance, 'activity', activityId);
    this.windowService.open(url);
  }

  /**
   * Navigate to a CRM entity detail page.
   * @param {string} entityType
   * @param {string} entityId
   */
  openEntity(entityType, entityId) {
    if (!entityType || !entityId || entityType === 'invalid') return;
    const url = this.urlBuilder.buildEntityUrl(getInstance(), entityType, entityId);
    this.windowService.open(url);
  }

  /**
   * Open the CRM create-contact drawer with the signature box contents.
   * @param {string} text
   * @returns {boolean} false when there is no tenant instance or empty text
   */
  openCreateContactFromSignature(text) {
    const url = buildCreateContactFromSignatureUrl(getInstance(), text);
    if (!url) return false;
    this.windowService.open(url);
    return true;
  }

  /**
   * Whether this client can navigate to the entity type (unknown types render as plain text).
   */
  isNavigableEntityType(entityType) {
    return KNOWN_ENTITY_TYPES.has(entityType);
  }

  async _resolveInstance(getInstanceFn) {
    try {
      if (typeof getInstanceFn === 'function') {
        return await getInstanceFn();
      }
      return getInstance();
    } catch {
      return null;
    }
  }
}

export const crmNavigation = new CRMNavigationService();
